<template>
  <div class="static-position">
    <label class="static-position__head" :class="{ 'static-position__head--open': enabled }">
      <span>
        <span class="block text-gray-700 text-sm font-bold font-serif">{{ $t("static_position.title") }}</span>
        <span class="block text-xs text-gray-500">{{ $t("static_position.subtitle") }}</span>
      </span>
      <input type="checkbox" class="switch flex-shrink-0 ml-4" :checked="enabled" @change="toggle($event.target.checked)" />
    </label>

    <div v-if="enabled" class="static-position__body">
      <p class="text-xs text-gray-500 mb-2">{{ $t("static_position.hint") }}</p>

      <div class="flex items-center mb-2">
        <GmapAutocomplete
          class="static-position__search"
          :placeholder="$t('static_position.search')"
          :value="value && value.address ? value.address : ''"
          :options="{ fields: ['geometry', 'formatted_address'] }"
          @place_changed="onPlace"
          @keydown.enter.prevent
        />
        <button v-if="hasPosition" type="button" class="btn-red-outline ml-2" @click.prevent="clear">
          {{ $t("static_position.clear") }}<BaseIcon icon="times" class="ml-1" />
        </button>
      </div>

      <GmapMap :center="center" :zoom="hasPosition ? 16 : 5" map-type-id="roadmap" class="static-position__map" :options="{ streetViewControl: false, mapTypeControl: false }">
        <GmapMarker v-if="hasPosition" :position="{ lat: value.lat, lng: value.lng }" :draggable="true" @dragend="onDrag" />
      </GmapMap>
      <p v-if="hasPosition" class="text-xs text-gray-500 mt-1">
        {{ $t("static_position.drag_hint") }} · {{ value.lat.toFixed(5) }}, {{ value.lng.toFixed(5) }}
      </p>
    </div>
  </div>
</template>

<script>
import { gmapApi } from "vue2-google-maps";

// Sweden, shown before a position is set
const DEFAULT_CENTER = { lat: 62.0, lng: 15.0 };

// v-model: { address, lat, lng } or null
export default {
  name: "StaticPositionPicker",
  props: {
    value: { type: Object, default: null },
  },
  data() {
    return {
      // on when the device already has a position; switching off clears it,
      // switching on again before saving brings it back
      enabled: !!this.value,
      lastValue: this.value,
    };
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.enabled = true;
        this.lastValue = newVal;
      }
    },
  },
  computed: {
    google: gmapApi,
    hasPosition() {
      return !!this.value && typeof this.value.lat === "number" && typeof this.value.lng === "number";
    },
    center() {
      return this.hasPosition ? { lat: this.value.lat, lng: this.value.lng } : DEFAULT_CENTER;
    },
  },
  methods: {
    onPlace(place) {
      if (!place || !place.geometry || !place.geometry.location) return;
      this.$emit("input", {
        address: place.formatted_address || "",
        lat: Number(place.geometry.location.lat().toFixed(6)),
        lng: Number(place.geometry.location.lng().toFixed(6)),
      });
    },

    // the pin was moved: keep the new point and look up its address
    async onDrag(event) {
      const lat = Number(event.latLng.lat().toFixed(6));
      const lng = Number(event.latLng.lng().toFixed(6));
      let address = this.value ? this.value.address : "";
      try {
        const geocoder = new this.google.maps.Geocoder();
        const response = await geocoder.geocode({ location: { lat, lng } });
        if (response && response.results && response.results[0]) address = response.results[0].formatted_address;
      } catch (error) {
        // keep the old address text; the coordinates are what the servers use
      }
      this.$emit("input", { address, lat, lng });
    },

    toggle(on) {
      this.enabled = on;
      this.$emit("input", on ? this.lastValue || null : null);
    },

    clear() {
      this.lastValue = null;
      this.$emit("input", null);
    },
  },
};
</script>

<style lang="scss">
.static-position {
  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
  }

  &__head--open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    border: 1px solid #e5e7eb;
    border-top: 0;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  &__search {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border: 2px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  &__map {
    width: 100%;
    height: 240px;
    border-radius: 0.375rem;
    overflow: hidden;
  }
}
</style>
